import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
interface ComparisonProps {
  beforeImage: string;
  afterImage: string;
  label: string;
  beforeLabel: string;
  afterLabel: string;
}
const Comparison = ({
  beforeImage,
  afterImage,
  label,
  beforeLabel,
  afterLabel
}: ComparisonProps) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const updatePosition = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, x / rect.width * 100));
    setPosition(percent);
  };
  const handleMouseDown = () => {
    isDragging.current = true;
  };
  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      updatePosition(clientX);
    };
    const handleUp = () => {
      isDragging.current = false;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, []);
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6
  }} className="flex flex-col gap-4" data-id="element-19">
      <div ref={containerRef} className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-xl select-none cursor-ew-resize bg-gray-100" onMouseDown={e => {
      handleMouseDown();
      updatePosition(e.clientX);
    }} onTouchStart={e => {
      handleMouseDown();
      updatePosition(e.touches[0].clientX);
    }} data-id="element-20">
        {/* After image (base layer) */}
        <img src={afterImage} alt={`${label} - after`} className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} data-id="element-21" />
        <span className="absolute top-4 right-4 bg-pink-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-md" data-id="element-22">
          {afterLabel}
        </span>

        {/* Before image (clipped overlay) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{
        width: `${position}%`
      }} data-id="element-23">
          <img src={beforeImage} alt={`${label} - before`} className="absolute inset-0 w-full h-full object-cover" style={{
          width: `${100 / position * 100}%`,
          maxWidth: 'none'
        }} draggable={false} data-id="element-24" />
          <span className="absolute top-4 left-4 bg-navy/80 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-md" data-id="element-25">
            {beforeLabel}
          </span>
        </div>

        {/* Slider handle */}
        <div className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_rgba(0,0,0,0.3)] pointer-events-none" style={{
        left: `${position}%`,
        transform: 'translateX(-50%)'
      }} data-id="element-26">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center" data-id="element-27">
            <div className="flex items-center gap-0.5 text-pink-primary" data-id="element-28">
              <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" data-id="element-29">
                <path d="M10 7L0 14V0z" transform="rotate(180 5 7)" data-id="element-30" />
              </svg>
              <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" data-id="element-31">
                <path d="M10 7L0 14V0z" data-id="element-32" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <p className="text-center font-semibold text-navy" data-id="element-33">{label}</p>
    </motion.div>;
};
export const BeforeAfter = () => {
  const {
    t
  } = useLanguage();
  const items: {
    label: string;
  }[] = t('beforeAfter.items') as any;
  const pairs = [{
    before: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    after: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
  }, {
    before: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    after: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
  }, {
    before: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    after: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
  }];
  return <section className="py-20 bg-white" data-id="element-34">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-35">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-36">
          <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-37">
            {t('beforeAfter.title')}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-38">
            {t('beforeAfter.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-39">
          {pairs.map((pair, idx) => <Comparison key={idx} beforeImage={pair.before} afterImage={pair.after} label={items[idx]?.label || ''} beforeLabel={t('beforeAfter.before')} afterLabel={t('beforeAfter.after')} data-id="element-40" />)}
        </div>
      </div>
    </section>;
};